import DUIField from "@/components/common/dui-field";
import SelectField from "@/components/common/select";
import { InternationalInputPhone } from "@/components/international-input-phone";
import useUserOrganizationCatalog from "@/hooks/catalogs/organization";
import useUserUnitCatalog from "@/hooks/catalogs/unit";
import useUserPositionCatalog from "@/hooks/catalogs/user-position";
import { useAuthorization } from "@/hooks/use-authorization";
import { useGetRolesAsInfinite } from "@/queries/roles";
import type { NewUserFormData } from "@/schemas/users/new-user";
import { Permissions } from "@/utils/permissions";
import { Heading, TextField } from "@elaniintc/goes-design-system";
import { Controller, useFormContext } from "react-hook-form";

interface Props {
  setSelectedRole: (role: { label: string; type: string } | null) => void;
  setSelectedUnit: (
    unit: {
      label: string;
      value: string;
    } | null,
  ) => void;
  setSelectedOrganization: (
    organization: {
      label: string;
      value: string;
    } | null,
  ) => void;
  setSelectedPosition: (
    position: {
      label: string;
      value: string;
    } | null,
  ) => void;
  onSubmit: (data: NewUserFormData) => void;
}

const UserForm = ({
  setSelectedRole,
  setSelectedUnit,
  setSelectedOrganization,
  setSelectedPosition,
  onSubmit,
}: Props) => {
  const { hasPermission } = useAuthorization();
  const canAssignRole = hasPermission(Permissions.users["assign-role"]);
  const canReadRole = hasPermission(Permissions.roles.read);
  const canUseRole = canAssignRole && canReadRole;

  const {
    data,
    hasNextPage: hasMoreRoles,
    fetchNextPage: loadMoreRoles,
    isFetching: isFetchingRoles,
    isLoading: isLoadingRoles,
  } = useGetRolesAsInfinite({
    page: 1,
    per_page: 20,
    sort_by: "name",
    sort_direction: "asc",
  });

  const roles =
    data?.pages.flatMap((page) =>
      page.data.map((role) => ({
        value: role.id,
        label: role.name,
        type: role.type,
      })),
    ) || [];

  const { position } = useUserPositionCatalog(true);
  const { organization } = useUserOrganizationCatalog(true);
  const { unit } = useUserUnitCatalog();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    register,
    watch,
    trigger,
  } = useFormContext<NewUserFormData>();

  const [role_id, unit_id, organization_id, position_id] = watch([
    "role_id",
    "department_id",
    "assigned_institution_id",
    "registered_position_id",
  ]);

  return (
    <form
      className="flex flex-col gap-6 border rounded-lg bg-neutral-object p-6 border-neutral-quiet"
      onSubmit={handleSubmit(onSubmit)}
      id="new-user-form"
    >
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Información personal</Heading>
        <div className="flex flex-col gap-4">
          <TextField
            id="first-name"
            label="Nombres"
            placeholder="Nombres"
            required
            {...register("name")}
          />
          <TextField
            id="last-name"
            label="Apellidos"
            placeholder="Apellidos"
            required
            {...register("last_name")}
          />
          <Controller
            control={control}
            name="dui"
            render={({ field: { ref, ...rest } }) => {
              return (
                <DUIField
                  {...rest}
                  id="dui-number"
                  label="Número de DUI"
                  placeholder="00000000-0"
                  required
                  formRef={ref}
                  error={errors.dui?.message}
                />
              );
            }}
          />
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Información de contacto</Heading>
        <div className="flex flex-col gap-4">
          <TextField
            id="email"
            label="Correo electrónico"
            placeholder="Correo electrónico"
            required
            error={errors.email?.message}
            {...register("email")}
          />
          <Controller
            name="phone_number"
            control={control}
            render={({ field: { ref, ...rest } }) => {
              return (
                <InternationalInputPhone
                  {...rest}
                  inputRef={ref}
                  error={errors.phone_number?.number?.message as string}
                />
              );
            }}
          />
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-4 @container">
        <Heading variant="h5">Otra información</Heading>
        <div className="flex flex-col gap-4">
          <SelectField
            label="Institución asignada"
            placeholder="Institución asignada"
            name="assigned_institution_id"
            required
            value={organization_id}
            options={organization}
            onChange={(value) => {
              setValue("assigned_institution_id", value);
              trigger("assigned_institution_id");
              const org = organization.find((o) => o.value === value);
              setSelectedOrganization(org ?? null);
            }}
            isLoading={false}
          />
          <SelectField
            label="Unidad o departamento"
            placeholder="Unidad o departamento"
            name="department_id"
            required
            value={unit_id}
            options={unit}
            onChange={(value) => {
              setValue("department_id", value);
              trigger("department_id");
              const unit_ = unit.find((u) => u.value === value);
              setSelectedUnit(unit_ ?? null);
            }}
            isLoading={false}
          />
          <SelectField
            label="Cargo registrado"
            placeholder="Cargo registrado"
            name="registered_position_id"
            required
            value={position_id}
            options={position}
            onChange={(value) => {
              setValue("registered_position_id", value);
              trigger("registered_position_id");
              const pos = position.find((p) => p.value === value);
              setSelectedPosition(pos ?? null);
            }}
            isLoading={false}
          />
        </div>
      </div>
      {canUseRole && (
        <>
          <div className="w-full h-px border-t border-neutral-quiet" />
          <div className="flex flex-col gap-4 @container">
            <Heading variant="h5">Configuración</Heading>
            <div className="flex flex-col gap-4">
              <SelectField
                label="Rol asignado"
                placeholder="Rol asignado"
                name="role_id"
                value={role_id || ""}
                options={roles}
                onChange={(value) => {
                  setValue("role_id", value);
                  trigger("role_id");
                  const role = roles.find((r) => r.value === value);
                  setSelectedRole(
                    role
                      ? {
                          label: role.label,
                          type: role.type,
                        }
                      : null,
                  );
                }}
                hasMore={hasMoreRoles}
                onLoadMore={loadMoreRoles}
                isDisabled={isLoadingRoles}
                isLoading={isLoadingRoles || isFetchingRoles}
              />
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default UserForm;
